import { Injectable, ApplicationRef } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MatSnackBar } from '@angular/material/snack-bar';
import { interval, concat, of } from 'rxjs';
import { first, switchMap, catchError, filter } from 'rxjs/operators';

interface VersionInfo {
  hash: string;
  buildTime: string;
  environment: string;
}

@Injectable({
  providedIn: 'root'
})
export class VersionCheckService {
  private currentVersion: string | null = null;
  private checkInterval = 5 * 60 * 1000; // Check every 5 min
  private promptShown = false;

  constructor(
    private http: HttpClient,
    private appRef: ApplicationRef,
    private snackBar: MatSnackBar
  ) {}

  /**
   * Initialize version checking
   * Call this from app.component.ts ngOnInit()
   */
  initVersionCheck(): void {
    // Load current version immediately
    this.loadCurrentVersion();

    // Wait for app to stabilize, then start periodic checks
    const appIsStable$ = this.appRef.isStable.pipe(
      first(isStable => isStable === true)
    );

    const everySomeMinutes$ = interval(this.checkInterval);

    const checkWhenStable$ = concat(
      appIsStable$,
      everySomeMinutes$
    );

    checkWhenStable$.pipe(
      switchMap(() => this.checkForNewVersion()),
      filter(hasNewVersion => hasNewVersion === true)
    ).subscribe(() => {
      this.promptUserToRefresh();
    });
  }

  /**
   * Load the current app version from assets
   */
  private loadCurrentVersion(): void {
    this.http.get<VersionInfo>('/assets/version.json').pipe(
      catchError(error => {
        // This is normal in development when version.json hasn't been generated yet
        console.warn('[Version Check] version.json not found - version checking disabled (run build to generate)');
        return of(null);
      })
    ).subscribe(version => {
      if (version) {
        this.currentVersion = version.hash;
        console.log('[Version Check] Current version:', this.currentVersion);
        console.log('[Version Check] Build time:', version.buildTime);
      }
    });
  }

  /**
   * Check if a new version is available on the server
   * Add cache-busting query param to avoid cached responses
   */
  private checkForNewVersion(): Promise<boolean> {
    const cacheBuster = new Date().getTime();

    return this.http.get<VersionInfo>(`/assets/version.json?t=${cacheBuster}`).pipe(
      catchError(error => {
        console.error('[Version Check] Failed to check version:', error);
        return of({ hash: this.currentVersion || 'error', buildTime: '', environment: '' } as VersionInfo);
      })
    ).toPromise().then(serverVersion => {
      if (!serverVersion || !this.currentVersion) {
        return false;
      }

      const hasNewVersion = serverVersion.hash !== this.currentVersion;

      if (hasNewVersion) {
        console.log('[Version Check] 🎉 New version detected!');
        console.log('[Version Check] Current:', this.currentVersion);
        console.log('[Version Check] Server:', serverVersion.hash);
        console.log('[Version Check] Build time:', serverVersion.buildTime);
      }

      return hasNewVersion;
    });
  }

  /**
   * Prompt user to refresh the page
   * Shows a Material Snackbar notification
   */
  private promptUserToRefresh(): void {
    // Prevent multiple prompts
    if (this.promptShown) {
      return;
    }
    this.promptShown = true;

    // Show persistent snackbar with action button
    const snackBarRef = this.snackBar.open(
      '🎉 New version available! Click to reload and get the latest features.',
      'Reload Now',
      {
        duration: 0, // Don't auto-dismiss
        horizontalPosition: 'center',
        verticalPosition: 'bottom',
        panelClass: ['update-snackbar']
      }
    );

    snackBarRef.onAction().subscribe(() => {
      this.reloadApp();
    });
  }

  /**
   * Reload the application
   * Performs a hard refresh to clear caches
   */
  private reloadApp(): void {
    console.log('[Version Check] Reloading application...');
    // Hard reload to clear cache
    window.location.reload();
  }

  /**
   * Manual version check
   * Useful for testing or "Check for Updates" button
   */
  public manualCheck(): void {
    this.checkForNewVersion().then(hasNewVersion => {
      if (hasNewVersion) {
        this.promptUserToRefresh();
      } else {
        console.log('[Version Check] You are running the latest version!');
      }
    });
  }
}
